import { useId, useMemo, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useMeasure } from "@/hooks/use-measure";
import type { HeadcountPoint } from "@/hooks/use-org-health";

// Plot padding (px). The plot itself sizes to the measured container so the
// chart fills whatever panel it's laid out in (side-by-side or full width).
const PAD = { top: 12, right: 16, bottom: 28, left: 40 };
const MIN_PLOT_HEIGHT = 220;

/** "2026-07" → "Jul" (with year on January so year boundaries stay readable). */
function monthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, 1));
  const name = d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  return m === 1 ? `${name} '${String(y).slice(2)}` : name;
}

/** "2026-07" → "Jul 2026" for the tooltip. */
function monthLabelLong(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, 1)).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** A rounded-up "nice" axis ceiling (10 → 10, 55 → 60, 130 → 150). */
function niceCeiling(maxValue: number): number {
  if (maxValue <= 0) return 10;
  const magnitude = 10 ** Math.floor(Math.log10(maxValue));
  for (const mult of [1, 1.5, 2, 3, 4, 5, 8, 10]) {
    if (maxValue <= magnitude * mult) return magnitude * mult;
  }
  return magnitude * 10;
}

const RANGE_OPTIONS = [
  { months: 12, label: "1y" },
  { months: 36, label: "3y" },
  { months: 60, label: "5y" },
] as const;

/**
 * Headcount-over-time line chart (single series, zero baseline). Months with
 * nightly snapshots draw solid; earlier months are derived approximations and
 * draw dashed with a caption explaining the estimate. Hand-built SVG in the
 * house DonutChart idiom — hover crosshair + tooltip, sr-only data table as
 * the non-visual fallback. The plot measures its container and renders 1:1,
 * so it fills the panel it's placed in.
 */
export function HeadcountTrendCard({
  points,
  months,
  onMonthsChange,
  isLoading,
  isError,
  onRetry,
}: {
  points: HeadcountPoint[] | undefined;
  months: number;
  onMonthsChange: (months: number) => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const titleId = useId();
  const [hover, setHover] = useState<number | null>(null);
  const { ref: plotRef, size } = useMeasure<HTMLDivElement>();

  const geometry = useMemo(() => {
    if (!points || points.length === 0 || size.width < 120 || size.height < 120) return null;
    const W = size.width;
    const H = size.height;
    const plotW = W - PAD.left - PAD.right;
    const plotH = H - PAD.top - PAD.bottom;
    const ceiling = niceCeiling(Math.max(...points.map((p) => p.count)));
    const stepX = points.length > 1 ? plotW / (points.length - 1) : 0;
    const xy = points.map((p, i) => ({
      ...p,
      x: PAD.left + (points.length > 1 ? i * stepX : plotW / 2),
      y: PAD.top + plotH - (p.count / ceiling) * plotH,
    }));
    // Split into contiguous derived/snapshot runs so the derived history draws
    // dashed. The boundary point is shared, keeping the line continuous.
    const segments: { dashed: boolean; pts: typeof xy }[] = [];
    for (const p of xy) {
      const dashed = p.source === "derived";
      const last = segments[segments.length - 1];
      if (last && last.dashed === dashed) {
        last.pts.push(p);
      } else {
        const boundary = last ? [last.pts[last.pts.length - 1]] : [];
        segments.push({
          dashed,
          pts: [...boundary.filter((b) => b !== undefined), p],
        });
      }
    }
    const gridSteps = [0.25, 0.5, 0.75, 1].map((f) => ({
      value: Math.round(ceiling * f),
      y: PAD.top + plotH - f * plotH,
    }));
    return { xy, segments, ceiling, gridSteps, W, H, plotH };
  }, [points, size.width, size.height]);

  const hasDerived = points?.some((p) => p.source === "derived") ?? false;
  const hovered = hover !== null ? (geometry?.xy[hover] ?? null) : null;

  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    if (!geometry) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    let best = 0;
    let bestDist = Number.POSITIVE_INFINITY;
    for (const [i, p] of geometry.xy.entries()) {
      const d = Math.abs(p.x - x);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    setHover(best);
  }

  return (
    <div className="flex flex-col rounded-lg border bg-card p-4 sm:p-6">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold text-foreground text-sm" id={titleId}>
          Headcount over time
        </h2>
        <fieldset className="flex items-center gap-1" aria-label="Time range">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.months}
              type="button"
              aria-pressed={months === opt.months}
              onClick={() => onMonthsChange(opt.months)}
              className={
                months === opt.months
                  ? "rounded-md border border-primary/30 bg-primary/10 px-2 py-0.5 font-medium text-primary text-xs"
                  : "rounded-md border bg-card px-2 py-0.5 font-medium text-muted-foreground text-xs transition-colors hover:text-foreground"
              }
            >
              {opt.label}
            </button>
          ))}
        </fieldset>
      </div>
      <p className="mt-0.5 text-muted-foreground text-xs">
        Active people at each month&rsquo;s end.
        {hasDerived && (
          <>
            {" "}
            <svg
              className="inline-block align-middle"
              width="18"
              height="6"
              role="img"
              aria-label="dashed line"
            >
              <title>dashed line</title>
              <line
                x1="1"
                y1="3"
                x2="17"
                y2="3"
                stroke="var(--chart-1)"
                strokeWidth="2"
                strokeDasharray="4 3"
              />
            </svg>{" "}
            Dashed months predate daily snapshots and are estimated from start dates and
            deactivation history.
          </>
        )}
      </p>

      {isLoading ? (
        <Skeleton className="mt-4 h-48 w-full flex-1 rounded-lg" />
      ) : isError ? (
        <div className="mt-4">
          <ErrorState message="Failed to load headcount trend" onRetry={onRetry} />
        </div>
      ) : (
        <div ref={plotRef} className="relative mt-4 flex-1" style={{ minHeight: MIN_PLOT_HEIGHT }}>
          {!geometry ? (
            (points?.length ?? 0) === 0 && !isLoading ? (
              <p className="text-muted-foreground text-sm">No headcount data yet.</p>
            ) : null
          ) : (
            <>
              <svg
                width={geometry.W}
                height={geometry.H}
                viewBox={`0 0 ${geometry.W} ${geometry.H}`}
                className="absolute inset-0"
                role="img"
                aria-labelledby={titleId}
                onMouseMove={onMove}
                onMouseLeave={() => setHover(null)}
              >
                <title>Headcount over time</title>
                {/* Recessive horizontal grid + y labels */}
                {geometry.gridSteps.map((g) => (
                  <g key={g.value}>
                    <line
                      x1={PAD.left}
                      y1={g.y}
                      x2={geometry.W - PAD.right}
                      y2={g.y}
                      stroke="var(--border)"
                      strokeWidth="1"
                    />
                    <text
                      x={PAD.left - 8}
                      y={g.y + 3}
                      textAnchor="end"
                      className="fill-muted-foreground"
                      fontSize="10"
                    >
                      {g.value}
                    </text>
                  </g>
                ))}
                {/* Zero baseline */}
                <line
                  x1={PAD.left}
                  y1={PAD.top + geometry.plotH}
                  x2={geometry.W - PAD.right}
                  y2={PAD.top + geometry.plotH}
                  stroke="var(--border)"
                  strokeWidth="1"
                />

                {/* Area fill under the whole series */}
                <path
                  d={`M ${geometry.xy.map((p) => `${p.x} ${p.y}`).join(" L ")} L ${
                    geometry.xy[geometry.xy.length - 1]?.x ?? 0
                  } ${PAD.top + geometry.plotH} L ${geometry.xy[0]?.x ?? 0} ${
                    PAD.top + geometry.plotH
                  } Z`}
                  fill="var(--chart-1)"
                  opacity="0.08"
                />

                {/* Line segments: dashed = derived, solid = snapshot */}
                {geometry.segments.map((seg) => (
                  <path
                    key={`${seg.dashed}-${seg.pts[0]?.month ?? ""}`}
                    d={`M ${seg.pts.map((p) => `${p.x} ${p.y}`).join(" L ")}`}
                    fill="none"
                    stroke="var(--chart-1)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray={seg.dashed ? "4 3" : undefined}
                  />
                ))}

                {/* Hover crosshair + marker */}
                {hovered && (
                  <g>
                    <line
                      x1={hovered.x}
                      y1={PAD.top}
                      x2={hovered.x}
                      y2={PAD.top + geometry.plotH}
                      stroke="var(--muted-foreground)"
                      strokeWidth="1"
                      opacity="0.4"
                    />
                    <circle
                      cx={hovered.x}
                      cy={hovered.y}
                      r="4.5"
                      fill="var(--chart-1)"
                      stroke="var(--card)"
                      strokeWidth="2"
                    />
                  </g>
                )}

                {/* X labels — every month up to a year; only Januaries beyond that
                    so long ranges don't collide */}
                {geometry.xy.map((p) => {
                  if (geometry.xy.length > 14 && !p.month.endsWith("-01")) return null;
                  return (
                    <text
                      key={p.month}
                      x={p.x}
                      y={geometry.H - 8}
                      textAnchor="middle"
                      className="fill-muted-foreground"
                      fontSize="10"
                    >
                      {geometry.xy.length > 14 ? p.month.slice(0, 4) : monthLabel(p.month)}
                    </text>
                  );
                })}
              </svg>

              {/* Tooltip (plot renders 1:1, so px positioning is exact) */}
              {hovered && (
                <div
                  className="pointer-events-none absolute z-10 rounded-md border bg-popover px-2.5 py-1.5 text-popover-foreground shadow-md"
                  style={{
                    left: hovered.x,
                    top: Math.max(hovered.y - 14, 0),
                    transform: "translate(-50%, -100%)",
                  }}
                >
                  <div className="font-medium text-xs">{monthLabelLong(hovered.month)}</div>
                  <div className="text-muted-foreground text-xs">
                    {hovered.count.toLocaleString()} people ·{" "}
                    {hovered.source === "snapshot" ? "exact" : "estimated"}
                  </div>
                </div>
              )}

              {/* Non-visual fallback. Named via aria-label rather than a
                  <caption> so no "Headcount by month" text node renders inside
                  the plot area. */}
              <table className="sr-only" aria-label="Headcount by month">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Headcount</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {geometry.xy.map((p) => (
                    <tr key={p.month}>
                      <td>{monthLabelLong(p.month)}</td>
                      <td>{p.count}</td>
                      <td>{p.source === "snapshot" ? "exact" : "estimated"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
